import React from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from '../authentication';
import { TileButton } from '../../components/Buttons';
import Breadcrumbs from '../../components/Breadcrumbs';
import HelpAndInfoBox from '../../components/HelpAndInfoBox';
import LoadingIndicator from '../../components/LoadingIndicator';
import { usePathwayModal } from './hooks/usePathwayModal';
import { ActionsContainer } from './components/ActionsContainer';
import SettingsHeader from '../../components/SettingsHeader';
import { PathwayTrainingItem } from './components/PathwayTrainingItem';
import {
  PageContainer,
  MainSectionContainer,
  HeaderButtonGroup,
  HeaderRow,
  PublishButton,
} from './components/LayoutElements';
import { AddTrainingListItem } from './components/AddTrainingListItem';
import { SortablePathwayList, SortablePathwayListItem } from './components/SortablePathwayList';
import { usePathwayBuilder } from './hooks/usePathwayBuilder';
import ErrorMessage from '../../components/ErrorMessage';
import useManageTrainingRoute from '../../hooks/useManageTrainingRoute';
import { handleError } from '../../utils/apiUtils';
import alertService from '../../services/AlertService';
import catalogService from '../../services/catalogService';
import { useEffect, useState } from 'react';

export const PathwayBuilder = () => {
  const { user } = useUser();
  const { trainingId } = useParams<{ trainingId: string }>();
  const { manageTrainingRoutePrefix, manageTrainingType } = useManageTrainingRoute();
  const isAuthoring = manageTrainingType === 'author';
  const [ownerId, setOwnerId] = useState(user?.lastSelectedAccount || '');

  useEffect(() => {
    if (isAuthoring) {
      catalogService.getCatalogIdForSite().then((catalogId: string) => setOwnerId(catalogId));
    }
  }, [isAuthoring]);

  const {
    pathway,
    tasklists,
    isLoading,
    isError,
    redirectToSettings,
    reorder,
    removeEntity,
    handleTrainingItemRedirect,
    copyPathway,
    publishPathway,
    unpublishPathway,
  } = usePathwayBuilder();

  const {
    modal,
    openAddStepModal,
    openRemoveStepModal,
    openEasyLinkModal,
    openPublishModal,
    openUnpublishModal,
    openCopyModal,
    openReplaceUnownedCourseModal,
    dismissModal,
  } = usePathwayModal();

  const crumbs = [
    { name: 'Church', route: '#/org' },
    { name: 'Manage Pathways', route: `#${manageTrainingRoutePrefix}/pathways` },
    { name: pathway?.name || '' },
  ];

  const hasPathwayEntities = pathway.entities?.length > 2;

  const handleRemoveEntity = (tasklistId: string) =>
    removeEntity(tasklistId)
      .then(() => {
        alertService.show('Training Item Removed');
      })
      .catch(handleError)
      .finally(dismissModal);

  return (
    <>
      <Breadcrumbs crumbs={crumbs} />
      {isLoading ? (
        <LoadingIndicator />
      ) : isError ? (
        <ErrorMessage />
      ) : (
        <PageContainer>
          {pathway && (
            <>
              {hasPathwayEntities && !pathway.published && (
                <HelpAndInfoBox
                  title="Publish Pathway"
                  description="When you are finished building this pathway, use the Publish button to make it available."
                >
                  <PublishButton onClick={() => openPublishModal(publishPathway)}>Publish</PublishButton>
                </HelpAndInfoBox>
              )}

              <HeaderRow>
                <SettingsHeader title={pathway.name} subtitle="Pathway" icon={<i className="fas fa-route"></i>} />
                <HeaderButtonGroup>
                  <TileButton onClick={redirectToSettings}>
                    <i className="fas fa-pencil-alt"></i> Edit Settings
                  </TileButton>
                  {hasPathwayEntities && (
                    <TileButton onClick={() => openCopyModal(name => copyPathway(name, pathway.owner))}>
                      <i className="fas fa-copy"></i>Copy Pathway
                    </TileButton>
                  )}
                </HeaderButtonGroup>
              </HeaderRow>

              <MainSectionContainer>
                <SortablePathwayList onSort={reorder}>
                  {sortingItemIndex => (
                    <>
                      <AddTrainingListItem
                        onAddTrainingClick={() => openAddStepModal(0, pathway.entities, tasklists, ownerId)}
                      />
                      {tasklists.map((tasklist, index) => (
                        <SortablePathwayListItem key={tasklist.id} index={index}>
                          <PathwayTrainingItem
                            isEmail={tasklist._type === 'Email'}
                            course={tasklist}
                            onClick={() => handleTrainingItemRedirect(tasklist)}
                            onRemove={() => openRemoveStepModal(tasklist, () => handleRemoveEntity(tasklist.id))}
                            onEdit={
                              tasklist.owner === ownerId || tasklist._type === 'Email'
                                ? undefined
                                : () => openReplaceUnownedCourseModal(pathway, tasklist, index, removeEntity)
                            }
                          />
                          {sortingItemIndex !== index && (
                            <AddTrainingListItem
                              onAddTrainingClick={() =>
                                openAddStepModal(index + 1, pathway.entities, tasklists, ownerId)
                              }
                            />
                          )}
                        </SortablePathwayListItem>
                      ))}
                    </>
                  )}
                </SortablePathwayList>

                <ActionsContainer
                  canAssign={hasPathwayEntities}
                  isPublished={pathway.published}
                  onEasyLinkClick={() => openEasyLinkModal(trainingId, pathway.name, user?.lastSelectedAccount || '')}
                  onUnpublishClick={() => openUnpublishModal(unpublishPathway)}
                />
              </MainSectionContainer>

              {modal}
            </>
          )}
        </PageContainer>
      )}
    </>
  );
};
